import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { collection, getDocs, doc, setDoc } from 'firebase/firestore';
import { students as emtStudents } from '../data/students';
import { paramedicStudents } from '../data/paramedicStudents';

const UserManagement = () => {
  const { currentUser, updateUserRole } = useAuth();
  const [users, setUsers] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('student');
  const [loading, setLoading] = useState(true);
  
  // Popup states
  const [activePopup, setActivePopup] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('email');
  
  const classOptions = useMemo(() => [
    { id: 'emt-101', name: 'EMT 101 BEX SUM 2025', students: emtStudents },
    { id: 'paramedic-201', name: 'Paramedic 201', students: paramedicStudents },
  ], []);

  // Real staff members that should exist in the system
  const expectedStaff = useMemo(() => [
    { email: 'ptucker18@ccc.edu', name: 'Patricia Tucker', role: 'Instructor', uid: 'wiYbPXWo4bODLV5V2O6wD3ZNREC3' },
    { email: 'mdaly5@ccc.edu', name: 'Mike Daly', role: 'Instructor', uid: 'o1MQ4IghoAeOyIwtB5LTnXTZGu83' },
    { email: 'msepulveda8@ccc.edu', name: 'Maria Sepulveda', role: 'Instructor', uid: 'MYxyZpTiMVONF5B7mQWiioXlDmk2' },
    { email: 'lkaczmarek@ccc.edu', name: 'Lawrence Kaczmarek', role: 'Admin', uid: 'ydhVtrAoD4YEIE29G1rsi0jgLiB2' },
    { email: 'kward2@ccc.edu', name: 'Kevin Ward', role: 'Instructor', uid: 'C6J4zM07iaYtuX0y9Gve6K8F13E3' },
    { email: 'jjoneschaney@ccc.edu', name: 'Dr. Joni Jones Chaney', role: 'Instructor', uid: 'fgRxPnjeF8f3zSvt71aUdf76b432' },
    { email: 'smis1@ccc.edu', name: 'Steve Mis', role: 'Instructor', uid: 'xfGyS2ocHwX215kxDco6lUft5vo1' },
    { email: 'jharmon@ccc.edu', name: 'Joe Harmon', role: 'Instructor', uid: '6uPwK38BJ8hset2V8x6kDSvGNGf1' },
    { email: 'hmeyers@ccc.edu', name: 'Harry Meyers', role: 'Admin', uid: 'A1dmb7RX4WYdjjvxP9Ew6b4ix303' },
  ], []);

  const fetchUsers = useCallback(async () => {
    const usersCollectionRef = collection(db, 'users');
    const data = await getDocs(usersCollectionRef);
    const allUsers = data.docs.map((doc) => {
      const userData = { ...doc.data(), id: doc.id };
      
      // Enrich student data with class information
      if (userData.role === 'student') {
        // Find which class this student belongs to
        const studentClass = classOptions.find(classOption => 
          classOption.students.some(student => student.email === userData.email)
        );
        userData.className = studentClass?.name || 'Unassigned';
        userData.classId = studentClass?.id || 'unassigned';
      }
      
      return userData;
    });

    // Check for missing staff members and add them if they don't exist
    const existingEmails = allUsers.map(user => user.email);
    const missingStaff = expectedStaff.filter(staff => 
      !existingEmails.includes(staff.email)
    );

    // Create missing staff documents using their actual UIDs
    for (const staff of missingStaff) {
      try {
        const userRef = doc(db, 'users', staff.uid); // Use the actual UID as document ID
        await setDoc(userRef, {
          email: staff.email,
          displayName: staff.name,
          role: staff.role,
          createdAt: new Date(),
          createdBy: 'system-sync',
          syncedFromFirebaseAuth: true
        });
        console.log(`Created staff user document for ${staff.name} (${staff.email})`);
        
        // Add to allUsers array
        allUsers.push({
          id: staff.uid,
          email: staff.email,
          displayName: staff.name,
          role: staff.role,
          createdAt: new Date(),
          createdBy: 'system-sync',
          syncedFromFirebaseAuth: true
        });
      } catch (error) {
        console.error(`Error creating staff member ${staff.name}:`, error);
      }
    }

    setUsers(allUsers);
    
    // Debug: Log all users and their roles
    console.log('All users:', allUsers.map(u => ({ email: u.email, role: u.role })));
    
    // Filter instructors and fetch their permissions
    const instructorsList = allUsers.filter(user => user.role === 'Instructor');
    console.log('Filtered instructors:', instructorsList.map(i => ({ email: i.email, role: i.role })));
    setInstructors(instructorsList);
    
    // Fetch instructor permissions
    if (instructorsList.length > 0) {
      const permissionsRef = collection(db, 'instructorPermissions');
      const permissionsSnap = await getDocs(permissionsRef);
      const permissionsData = {};
      
      permissionsSnap.docs.forEach(doc => {
        permissionsData[doc.id] = doc.data();
      });

      // Set default permissions for instructors without existing permissions
      for (const instructor of instructorsList) {
        if (!permissionsData[instructor.id]) {
          permissionsData[instructor.id] = {
            generalAccess: [], // Array of modules with general access
            specialAccess: [], // Array of modules with special assignment access
            lastUpdated: new Date(),
            updatedBy: currentUser?.uid || 'unknown'
          };
        }
      }
      
      setPermissions(permissionsData);
    }
    setLoading(false);
  }, [currentUser, classOptions, expectedStaff]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleRoleChange = async (uid, newRole) => {
    try {
      await updateUserRole(uid, newRole);
      await fetchUsers(); // Refresh all data including instructor permissions
    } catch (error) {
      console.error("Failed to update role: ", error);
      alert("Failed to update role: " + error.message);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newEmail) {
      alert("Please enter an email.");
      return;
    }
    // This is a simplified example. In a real-world application, you would use a 
    // Firebase Function to securely create a user and their associated data.
    // Since we can't get a user's UID from their email on the client-side, 
    // we'll assume the user document ID is the email for this example.
    // This is NOT secure and should be replaced with a proper backend implementation.
    try {
      const userRef = doc(db, 'users', newEmail);
      await setDoc(userRef, {
        email: newEmail,
        role: newRole,
        createdAt: new Date()
      });
      setNewEmail('');
      setNewRole('student');
      fetchUsers();
      alert(`User document created for ${newEmail}. They will need to sign in to complete the process.`);
    } catch (error) {
      console.error("Error adding user document: ", error);
      alert("Failed to add user document: " + error.message);
    }
  };

  const handleDeleteUser = async (uid) => {
    if (window.confirm("Are you sure you want to delete this user? This action cannot be undone.")) {
        alert("User deletion from client-side is not directly supported for security reasons. Please use Firebase Admin SDK or Firebase Console to delete users from Firebase Authentication and then remove their Firestore document.");
    }
  };

  // Available modules in the system
  const availableModules = useMemo(() => [
    { id: 'skillSheets', name: 'Skill Sheets', icon: '📋' },
    { id: 'scenarioBuilder', name: 'Scenario Builder', icon: '🎭' },
  ], []);

  // Instructor Permission Functions
  const updateModuleAccess = useCallback(async (instructorId, moduleId, accessType) => {
    try {
      const currentPermissions = permissions[instructorId] || {};
      const generalAccess = [...(currentPermissions.generalAccess || [])];
      const specialAccess = [...(currentPermissions.specialAccess || [])];
      
      // Remove from both arrays first
      const generalIndex = generalAccess.indexOf(moduleId);
      const specialIndex = specialAccess.indexOf(moduleId);
      
      if (generalIndex > -1) generalAccess.splice(generalIndex, 1);
      if (specialIndex > -1) specialAccess.splice(specialIndex, 1);
      
      // Add to the appropriate array based on accessType
      if (accessType === 'general') {
        generalAccess.push(moduleId);
      } else if (accessType === 'special') {
        specialAccess.push(moduleId);
      }
      // If accessType is 'none', we just remove from both (already done above)

      const updatedPermissions = {
        ...currentPermissions,
        generalAccess,
        specialAccess,
        lastUpdated: new Date(),
        updatedBy: currentUser?.uid || 'unknown'
      };

      // Save to Firestore
      const permissionRef = doc(db, 'instructorPermissions', instructorId);
      await setDoc(permissionRef, updatedPermissions, { merge: true });

      // Update local state
      setPermissions(prev => ({
        ...prev,
        [instructorId]: updatedPermissions
      }));

      console.log(`Updated ${moduleId} access for instructor ${instructorId}: ${accessType}`);
    } catch (error) {
      console.error('Error updating module access:', error);
      alert('Error updating module access');
    }
  }, [permissions, currentUser]);

  const addSpecialAssignment = useCallback(async (instructorId) => {
    // This function is no longer needed but kept for compatibility
    console.log('Special assignments are now handled via module access checkboxes');
  }, []);

  const removeSpecialAssignment = useCallback(async (instructorId, assignmentId) => {
    // This function is no longer needed but kept for compatibility
    console.log('Special assignments are now handled via module access checkboxes');
  }, []);

  // Helper functions for filtering and sorting
  const getFilteredUsers = (role) => {
    let roleUsers = users.filter(user => user.role === role);
    
    // Apply search filter
    if (searchTerm) {
      roleUsers = roleUsers.filter(user => 
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.className && user.className.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Apply sorting
    roleUsers.sort((a, b) => {
      switch (sortBy) {
        case 'email':
          return a.email.localeCompare(b.email);
        case 'className':
          return (a.className || '').localeCompare(b.className || '');
        case 'createdAt':
          const dateA = a.createdAt ? new Date(a.createdAt.seconds * 1000) : new Date(0);
          const dateB = b.createdAt ? new Date(b.createdAt.seconds * 1000) : new Date(0);
          return dateB - dateA; // Newest first
        default:
          return 0;
      }
    });
    
    return roleUsers;
  };

  // Get unassigned users (users without proper class assignment)
  const getUnassignedUsers = () => {
    return users.filter(user => 
      !user.role || 
      user.role === 'student' && (!user.classId || user.classId === 'unassigned')
    );
  };

  // Get role counts
  const getRoleCounts = () => {
    const counts = {
      owner: users.filter(u => u.role === 'owner').length,
      Admin: users.filter(u => u.role === 'Admin').length,
      Instructor: users.filter(u => u.role === 'Instructor').length,
      student: users.filter(u => u.role === 'student').length,
      unassigned: getUnassignedUsers().length
    };
    return counts;
  };

  return (
    <div className="min-h-screen bg-bg text-text p-6">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-primary">User Management</h1>
        <button
          onClick={() => window.history.back()}
          className="bg-gray-500 text-white px-4 py-2 rounded shadow hover:bg-gray-700 font-semibold transition duration-200"
        >
          ← Back to Admin Dashboard
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="text-lg">Loading user data...</div>
        </div>
      ) : (
        <>
          {/* Main Management Buttons */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">User Management Dashboard</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {/* Role Management Buttons */}
              {['owner', 'Admin', 'Instructor', 'student'].map(role => {
                const count = getRoleCounts()[role];
                const roleDisplayName = role === 'owner' ? 'Owners' : 
                                      role === 'Admin' ? 'Administrators' : 
                                      role === 'Instructor' ? 'Instructors' : 'Students';
                
                return (
                  <button
                    key={role}
                    onClick={() => setActivePopup(role)}
                    className={`p-4 bg-white rounded-xl shadow-lg border-2 border-gray-200 hover:border-primary hover:shadow-xl transform hover:scale-105 transition-all duration-200 text-center group ${
                      role === 'owner' ? 'hover:bg-purple-50' :
                      role === 'Admin' ? 'hover:bg-red-50' :
                      role === 'Instructor' ? 'hover:bg-blue-50' :
                      'hover:bg-green-50'
                    }`}
                  >
                    <div className={`text-2xl font-bold mb-1 transition-colors duration-200 ${
                      role === 'owner' ? 'text-purple-600' :
                      role === 'Admin' ? 'text-red-600' :
                      role === 'Instructor' ? 'text-blue-600' :
                      'text-green-600'
                    }`}>
                      {count}
                    </div>
                    <div className="text-sm font-semibold text-gray-800 group-hover:text-primary transition-colors">
                      {roleDisplayName}
                    </div>
                  </button>
                );
              })}

              {/* Unassigned Users Button */}
              <button
                onClick={() => setActivePopup('unassigned')}
                className="p-4 bg-white rounded-xl shadow-lg border-2 border-gray-200 hover:border-orange-500 hover:shadow-xl hover:bg-orange-50 transform hover:scale-105 transition-all duration-200 text-center group"
              >
                <div className="text-2xl font-bold mb-1 text-orange-600 transition-colors duration-200">
                  {getRoleCounts().unassigned}
                </div>
                <div className="text-sm font-semibold text-gray-800 group-hover:text-orange-600 transition-colors">
                  Unassigned
                </div>
              </button>
            </div>

            {/* Action Buttons Row */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => setActivePopup('inviteUser')}
                className="p-4 bg-gradient-to-r from-green-500 to-teal-600 text-white rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                <div className="text-xl font-semibold">+ Invite New User</div>
                <div className="text-green-100 mt-1">Add new users to the system</div>
              </button>
              
              <button
                onClick={() => setActivePopup('instructorPermissions')}
                className="p-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                <div className="text-xl font-semibold">Permissions</div>
                <div className="text-indigo-100 mt-1">Instructor Access Control</div>
              </button>
            </div>
          </div>
        </>
      )}

      {/* Role Popup Modal */}
      {activePopup && !['instructorPermissions', 'unassigned', 'inviteUser'].includes(activePopup) && (
        <RolePopupModal
          role={activePopup}
          users={getFilteredUsers(activePopup)}
          onClose={() => setActivePopup(null)}
          onRoleChange={handleRoleChange}
          onDeleteUser={handleDeleteUser}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          sortBy={sortBy}
          setSortBy={setSortBy}
        />
      )}

      {/* Unassigned Users Popup */}
      {activePopup === 'unassigned' && (
        <UnassignedUsersPopup
          users={getUnassignedUsers()}
          onClose={() => setActivePopup(null)}
          onRoleChange={handleRoleChange}
          onDeleteUser={handleDeleteUser}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          sortBy={sortBy}
          setSortBy={setSortBy}
        />
      )}

      {/* Invite User Popup */}
      {activePopup === 'inviteUser' && (
        <InviteUserPopup
          onClose={() => setActivePopup(null)}
          onAddUser={handleAddUser}
          newEmail={newEmail}
          setNewEmail={setNewEmail}
          newRole={newRole}
          setNewRole={setNewRole}
        />
      )}

      {/* Instructor Permissions Popup */}
      {activePopup === 'instructorPermissions' && (
        <InstructorPermissionsPopup
          instructors={instructors}
          permissions={permissions}
          availableModules={availableModules}
          onClose={() => setActivePopup(null)}
          updateModuleAccess={updateModuleAccess}
        />
      )}
    </div>
  );
};

// Role Popup Modal Component
const RolePopupModal = ({ role, users, onClose, onRoleChange, onDeleteUser, searchTerm, setSearchTerm, sortBy, setSortBy }) => {
  const [expandedClasses, setExpandedClasses] = React.useState(new Set());

  // Prevent background scrolling when modal is open
  React.useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const roleDisplayName = role === 'owner' ? 'Owners' : 
                          role === 'Admin' ? 'Administrators' : 
                          role === 'Instructor' ? 'Instructors' : 'Students';

  const toggleClass = (className) => {
    const newExpanded = new Set(expandedClasses);
    if (newExpanded.has(className)) {
      newExpanded.delete(className);
    } else {
      newExpanded.add(className);
    }
    setExpandedClasses(newExpanded);
  };

  // Group users by class (only for students)
  const getUsersByClass = () => {
    if (role !== 'student') {
      return { 'All Users': users };
    }

    const grouped = {};
    users.forEach(user => {
      const className = user.className || 'Unassigned';
      if (!grouped[className]) {
        grouped[className] = [];
      }
      grouped[className].push(user);
    });

    // Sort classes: assigned classes first, then unassigned
    const sortedClasses = Object.keys(grouped).sort((a, b) => {
      if (a === 'Unassigned') return 1;
      if (b === 'Unassigned') return -1;
      return a.localeCompare(b);
    });

    const sortedGrouped = {};
    sortedClasses.forEach(className => {
      sortedGrouped[className] = grouped[className];
    });

    return sortedGrouped;
  };

  const usersByClass = getUsersByClass();
  const isStudentRole = role === 'student';

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
    >
      <div 
        className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`px-6 py-4 text-white ${
          role === 'owner' ? 'bg-purple-600' :
          role === 'Admin' ? 'bg-red-600' :
          role === 'Instructor' ? 'bg-blue-600' :
          'bg-green-600'
        }`}>
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">{roleDisplayName} Management</h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors text-2xl"
            >
              ×
            </button>
          </div>
          <p className="text-white/80 mt-1">Manage users with {role} role ({users.length} users)</p>
        </div>

        {/* Search and Sort Controls */}
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <input
                type="text"
                placeholder="Search by email or class..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                <option value="email">Email</option>
                <option value="className">Class</option>
                <option value="createdAt">Date Created</option>
              </select>
            </div>
          </div>
        </div>

        {/* Content */}
        <div 
          className="overflow-y-auto max-h-[60vh]"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#9CA3AF #F3F4F6'
          }}
          onWheel={(e) => e.stopPropagation()}
        >
          {users.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-500">
                <div className="text-lg font-semibold">No {roleDisplayName.toLowerCase()} found</div>
                <p className="mt-2">
                  {searchTerm ? 'Try adjusting your search terms.' : `No users have been assigned the ${role} role yet.`}
                </p>
              </div>
            </div>
          ) : isStudentRole ? (
            // Student view with collapsible classes
            <div className="p-4 space-y-3">
              {Object.entries(usersByClass).map(([className, classUsers]) => {
                const isExpanded = expandedClasses.has(className);
                
                return (
                  <div key={className} className="border border-gray-200 rounded-lg overflow-hidden transition-all duration-300 ease-in-out">
                    {/* Class Header - Always Visible */}
                    <div 
                      className="bg-gray-50 p-3 cursor-pointer hover:bg-gray-100 transition-colors duration-200"
                      onClick={() => toggleClass(className)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <span className="text-md transition-transform duration-200 ease-in-out">
                            {isExpanded ? '▼' : '▶'}
                          </span>
                          <div>
                            <h3 className="text-md font-semibold text-gray-800">
                              {className}
                            </h3>
                            <p className="text-xs text-gray-600">{classUsers.length} students</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            className === 'Unassigned' 
                              ? 'bg-gray-100 text-gray-800' 
                              : className.includes('EMT') 
                                ? 'bg-blue-100 text-blue-800' 
                                : 'bg-green-100 text-green-800'
                          }`}>
                            {classUsers.length} students
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Class Students - Expandable */}
                    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      isExpanded ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
                    }`}>
                      <div className="bg-white">
                        <table className="min-w-full table-auto">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {classUsers.map((user) => (
                              <tr key={user.id} className="hover:bg-gray-50 transition-colors duration-150">
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 break-all">{user.email}</td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <select
                                    value={user.role}
                                    onChange={(e) => onRoleChange(user.id, e.target.value)}
                                    className="border border-gray-300 rounded px-2 py-1 bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all duration-200"
                                  >
                                    <option value="student">Student</option>
                                    <option value="Instructor">Instructor</option>
                                    <option value="Admin">Admin</option>
                                    <option value="owner">Owner</option>
                                  </select>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {user.createdAt ? new Date(user.createdAt.seconds * 1000).toLocaleDateString() : 'Unknown'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <button
                                    onClick={() => onDeleteUser(user.id)}
                                    className="bg-red-500 text-white px-3 py-1 rounded text-sm hover:bg-red-600 transition-all duration-200 transform hover:scale-105"
                                  >
                                    Delete
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            // Non-student view (original table format)
            <table className="min-w-full table-auto">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 break-all">{user.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={user.role}
                        onChange={(e) => onRoleChange(user.id, e.target.value)}
                        className="border border-gray-300 rounded px-2 py-1 bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 transition"
                      >
                        <option value="student">Student</option>
                        <option value="Instructor">Instructor</option>
                        <option value="Admin">Admin</option>
                        <option value="owner">Owner</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.createdAt ? new Date(user.createdAt.seconds * 1000).toLocaleDateString() : 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => onDeleteUser(user.id)}
                        className="bg-red-500 text-white px-3 py-1 rounded text-sm hover:bg-red-600 transition-all duration-200"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center">
            {isStudentRole && (
              <div className="text-sm text-gray-600">
                Click on class names to expand/collapse student lists
              </div>
            )}
            <button
              onClick={onClose}
              className="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600 transition-all duration-200 ml-auto"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Unassigned Users Popup Component
const UnassignedUsersPopup = ({ users, onClose, onRoleChange, onDeleteUser, searchTerm, setSearchTerm, sortBy, setSortBy }) => {
  // Prevent background scrolling when modal is open
  React.useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-in fade-in duration-200"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div 
        className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-red-500 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">Unassigned Users Management</h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors text-2xl"
            >
              ×
            </button>
          </div>
          <p className="text-orange-100 mt-1">Users without proper role or class assignment ({users.length} users)</p>
        </div>

        {/* Search and Sort Controls */}
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <input
                type="text"
                placeholder="Search by email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                <option value="email">Email</option>
                <option value="createdAt">Date Created</option>
              </select>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[60vh]">
          {users.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-500">
                <div className="text-lg font-semibold">No unassigned users</div>
                <p className="mt-2">All users have been properly assigned roles and classes.</p>
              </div>
            </div>
          ) : (
            <table className="min-w-full table-auto">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Class Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 break-all">{user.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.role || 'No Role'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                        {user.className || 'Unassigned'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={user.role || ''}
                        onChange={(e) => onRoleChange(user.id, e.target.value)}
                        className="border border-gray-300 rounded px-2 py-1 bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 transition mr-2"
                      >
                        <option value="">Select Role</option>
                        <option value="student">Student</option>
                        <option value="Instructor">Instructor</option>
                        <option value="Admin">Admin</option>
                        <option value="owner">Owner</option>
                      </select>
                      <button
                        onClick={() => onDeleteUser(user.id)}
                        className="bg-red-500 text-white px-3 py-1 rounded text-sm hover:bg-red-600 transition-all duration-200"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600 transition-all duration-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Invite User Popup Component
const InviteUserPopup = ({ onClose, newEmail, setNewEmail, newRole, setNewRole, onAddUser }) => {
  // Prevent background scrolling when modal is open
  React.useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-in fade-in duration-200"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div 
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-secondary px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">Invite New User</h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors text-2xl"
            >
              ×
            </button>
          </div>
          <p className="text-primary-light mt-1">Add a new user to the system</p>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-yellow-800 text-sm">
              <strong>Note:</strong> User creation is only supported via the Firebase Console or Admin SDK. 
              This form is for reference only.
            </p>
          </div>

          <form onSubmit={onAddUser} className="space-y-4">
            <div>
              <label htmlFor="popup-new-user-email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                id="popup-new-user-email"
                type="email"
                placeholder="e.g. user@example.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition"
                autoComplete="off"
              />
            </div>
            
            <div>
              <label htmlFor="popup-new-user-role" className="block text-sm font-medium text-gray-700 mb-1">
                Assign Role
              </label>
              <select
                id="popup-new-user-role"
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition"
              >
                <option value="student">Student</option>
                <option value="Instructor">Instructor</option>
                <option value="Admin">Admin</option>
                <option value="owner">Owner</option>
              </select>
            </div>

            <div className="pt-4">
              <button
                type="submit"
                className="w-full bg-gray-400 text-white font-semibold py-3 rounded shadow transition-all duration-200 cursor-not-allowed opacity-70"
                title="User creation is only supported via the Firebase Console or Admin SDK."
                disabled
              >
                Add User (Disabled)
              </button>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600 transition-all duration-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Instructor Permissions Popup Component
const InstructorPermissionsPopup = ({ instructors, permissions, availableModules, onClose, updateModuleAccess }) => {
  const [expandedInstructors, setExpandedInstructors] = React.useState(new Set());

  // Prevent background scrolling when modal is open
  React.useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const toggleInstructor = (instructorId) => {
    const newExpanded = new Set(expandedInstructors);
    if (newExpanded.has(instructorId)) {
      newExpanded.delete(instructorId);
    } else {
      newExpanded.add(instructorId);
    }
    setExpandedInstructors(newExpanded);
  };

  const getModuleAccessType = (instructorPermissions, moduleId) => {
    const generalAccess = instructorPermissions.generalAccess || [];
    const specialAccess = instructorPermissions.specialAccess || [];
    
    if (generalAccess.includes(moduleId)) return 'general';
    if (specialAccess.includes(moduleId)) return 'special';
    return 'none';
  };

  const getInstructorSummary = (instructorPermissions) => {
    const generalAccess = instructorPermissions.generalAccess || [];
    const specialAccess = instructorPermissions.specialAccess || [];
    const totalAccess = generalAccess.length + specialAccess.length;
    
    if (totalAccess === 0) return 'No access';
    
    const parts = [];
    if (generalAccess.length > 0) parts.push(`${generalAccess.length} General`);
    if (specialAccess.length > 0) parts.push(`${specialAccess.length} Special`);
    
    return parts.join(', ');
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-in fade-in duration-200"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div 
        className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">Instructor Permissions Management</h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors text-2xl"
            >
              ×
            </button>
          </div>
          <p className="text-indigo-100 mt-1">Configure module access for instructors</p>
        </div>

        {/* Instructions */}
        <div className="p-4 bg-blue-50 border-b border-blue-200">
          <h3 className="font-semibold text-blue-800 mb-2">Permission Types:</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-800 font-semibold">General Access</span>
              <span className="text-blue-700">View others' content (read-only) + create/edit own</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 rounded text-xs bg-red-100 text-red-800 font-semibold">Special Assignment</span>
              <span className="text-blue-700">Full access to all content (tracked as "on the clock")</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[60vh] p-6">
          {instructors.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-500">
                <div className="text-lg font-semibold">No instructors found</div>
                <p className="mt-2">Assign the "Instructor" role to users first to manage their permissions.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {instructors.map(instructor => {
                const instructorPermissions = permissions[instructor.id] || {};
                const isExpanded = expandedInstructors.has(instructor.id);
                const summary = getInstructorSummary(instructorPermissions);
                
                return (
                  <div key={instructor.id} className="border border-gray-200 rounded-lg overflow-hidden transition-all duration-300 ease-in-out">
                    {/* Instructor Header - Always Visible */}
                    <div 
                      className="bg-gray-50 p-3 cursor-pointer hover:bg-gray-100 transition-colors duration-200"
                      onClick={() => toggleInstructor(instructor.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <span className="text-md transition-transform duration-200 ease-in-out">
                            {isExpanded ? '▼' : '▶'}
                          </span>
                          <div>
                            <h3 className="text-md font-semibold text-gray-800">
                              {instructor.displayName || instructor.email}
                            </h3>
                            <p className="text-xs text-gray-600">{instructor.email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="text-xs text-gray-600">{summary}</span>
                        </div>
                      </div>
                    </div>

                    {/* Permissions Detail - Expandable */}
                    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      isExpanded ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
                    }`}>
                      <div className="bg-white p-4 border-t border-gray-200">
                        <div className="space-y-4">
                          {availableModules.map(module => {
                            const accessType = getModuleAccessType(instructorPermissions, module.id);
                            
                            return (
                              <div key={module.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-3">
                                    <span className="text-2xl">{module.icon}</span>
                                    <div>
                                      <h4 className="text-md font-semibold text-gray-800">{module.name}</h4>
                                      <p className="text-sm text-gray-600">Configure access level for this module</p>
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-center space-x-6">
                                    {/* General Access Option */}
                                    <label className="flex items-center space-x-2 cursor-pointer">
                                      <input
                                        type="radio"
                                        name={`${instructor.id}-${module.id}`}
                                        value="general"
                                        checked={accessType === 'general'}
                                        onChange={() => updateModuleAccess(instructor.id, module.id, 'general')}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                                      />
                                      <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-800 font-semibold">
                                        General Access
                                      </span>
                                    </label>

                                    {/* Special Assignment Option */}
                                    <label className="flex items-center space-x-2 cursor-pointer">
                                      <input
                                        type="radio"
                                        name={`${instructor.id}-${module.id}`}
                                        value="special"
                                        checked={accessType === 'special'}
                                        onChange={() => updateModuleAccess(instructor.id, module.id, 'special')}
                                        className="h-4 w-4 text-red-600 focus:ring-red-500"
                                      />
                                      <span className="px-2 py-1 rounded text-xs bg-red-100 text-red-800 font-semibold">
                                        Special Assignment
                                      </span>
                                    </label>

                                    {/* No Access Option */}
                                    <label className="flex items-center space-x-2 cursor-pointer">
                                      <input
                                        type="radio"
                                        name={`${instructor.id}-${module.id}`}
                                        value="none"
                                        checked={accessType === 'none'}
                                        onChange={() => updateModuleAccess(instructor.id, module.id, 'none')}
                                        className="h-4 w-4 text-gray-600 focus:ring-gray-500"
                                      />
                                      <span className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-800 font-semibold">
                                        No Access
                                      </span>
                                    </label>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Click on instructor names to expand/collapse their permissions
            </div>
            <button
              onClick={onClose}
              className="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600 transition-all duration-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
